import React from 'react';
import { IBlock, IBlockData } from '@core/typings';
import { BasicType } from '@core/constants';
import { createBlock } from '@core/utils/createBlock';
import { merge } from 'lodash';
import { t, resolvePreviewColor, isDynamicListingColumn } from '@core/utils';
import { listingScopeCanvasClass } from '@core/utils/listingScopeCanvasClass';
import { getListingClassId } from '@core/utils/listingDataAttr';
import { classnames } from '@core/utils/classnames';
import { BasicBlock } from '@core/components/BasicBlock';

export type IColumn = IBlockData<
	{
		'background-color'?: string;
		border?: string;
		'border-radius'?: string;
		'inner-border'?: string;
		'inner-border-radius'?: string;
		padding?: string;
		'text-align'?: string;
		'vertical-align'?: string;
		width?: string;
		'border-width'?: string;
		'border-style'?: string;
		'border-color'?: string;
		'border-top-width'?: string;
		'border-right-width'?: string;
		'border-bottom-width'?: string;
		'border-left-width'?: string;
	},
	{
		selectedListingId?: string;
		selectedListing?: Record<string, unknown> | null;
		// Switched to "Listing" in the column panel: this column owns its own
		// listing for everything inside it. Only settable inside a Listing
		// Wrapper that has no listing of its own — see the Column attribute panel.
		isDynamicListing?: boolean;
	}
>;

export const COLUMN_DEFAULT_ATTRIBUTES: IColumn['attributes'] = {
	padding: '0px 0px 0px 0px',
	border: 'none',
	'vertical-align': 'top',
	'border-width': '0px',
	'border-style': 'solid',
	'border-color': '#000000',
	'border-top-width': '',
	'border-right-width': '',
	'border-bottom-width': '',
	'border-left-width': '',
	'background-color': '#FFFFFF',
};

/**
 * Collapses the per-side border controls into the border attributes mj-column
 * understands.
 */
export function resolveColumnAttributes(
	params: Parameters<NonNullable<IBlock['render']>>[0],
): IColumn['attributes'] {
	const data = params.data as IColumn;
	const borderWidth = data.attributes['border-width'];
	const borderStyle = data.attributes['border-style'];
	const borderColor = resolvePreviewColor(
		data.attributes['border-color'],
		params.mode,
		params.dataSource,
	);
	const borderTopWidth = data.attributes['border-top-width'];
	const borderRightWidth = data.attributes['border-right-width'];
	const borderBottomWidth = data.attributes['border-bottom-width'];
	const borderLeftWidth = data.attributes['border-left-width'];

	const hasSideBorderWidths = [
		borderTopWidth,
		borderRightWidth,
		borderBottomWidth,
		borderLeftWidth,
	].some(value => value !== undefined && value !== null && value !== '');

	if (hasSideBorderWidths) {
		const normalizedBorderStyle = borderStyle || 'solid';
		const normalizedBorderColor = borderColor || '#000000';

		return {
			...data.attributes,
			border: 'none',
			'border-top': `${borderTopWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
			'border-right': `${borderRightWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
			'border-bottom': `${borderBottomWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
			'border-left': `${borderLeftWidth || '0px'} ${normalizedBorderStyle} ${normalizedBorderColor}`,
		} as IColumn['attributes'];
	}

	if (borderWidth || borderStyle || borderColor) {
		return {
			...data.attributes,
			border: [borderWidth || '0px', borderStyle || 'solid', borderColor || '#000000'].join(
				' ',
			),
		};
	}

	return data.attributes;
}

export const Column = createBlock<IColumn>({
	get name() {
		return t('Column');
	},
	type: BasicType.COLUMN,
	create: payload => {
		const defaultData: IColumn = {
			type: BasicType.COLUMN,
			data: {
				value: {},
			},
			attributes: { ...COLUMN_DEFAULT_ATTRIBUTES },
			children: [],
		};
		return merge(defaultData, payload);
	},
	validParentType: [BasicType.SECTION, BasicType.GROUP],

	render(params) {
		const { data } = params;
		// A column in "Listing" mode is a listing scope, so it gets the same
		// canvas badge the wrapper does (testing mode only).
		let attributes = isDynamicListingColumn(data)
			? listingScopeCanvasClass(resolveColumnAttributes(params), params)
			: resolveColumnAttributes(params);

		const boundListingId = data.data?.value?.selectedListingId;
		// Same mj-html-attributes workaround as the Listing Wrapper - mj-column's
		// attribute schema drops data-* the same way mj-wrapper/mj-image do, so
		// css-class is stamped here and matched in JsonToMjml.tsx to re-attach
		// data-listing-id onto this same compiled element.
		//
		// Keyed off the listing id itself, NOT params.idx: unlike the Listing
		// Wrapper (registered directly), a "column switched to Listing" can
		// compile as advanced_column, which re-renders the base column through
		// generateAdvancedLayoutBlock with idx hard-coded to null - so an
		// idx-based token here would never match the idx-based selector
		// JsonToMjml.tsx computes by walking the JSON tree.
		if (params.mode === 'production' && isDynamicListingColumn(data) && boundListingId) {
			attributes = {
				...attributes,
				'css-class': classnames(attributes['css-class'], getListingClassId(boundListingId)),
			};
		}

		return (
			<BasicBlock
				params={{
					...params,
					data: {
						...data,
						attributes,
					},
				}}
				tag='mj-column'
			/>
		);
	},
});
